import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  LayoutDashboard,
  Users,
  DollarSign,
  Calendar,
  BookOpen,
  MessageSquare,
  BarChart3,
  Settings,
  ChevronUp,
  LogOut,
  User,
  Church,
  Moon,
  Sun,
  Shield,
  Heart,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Package,
  Building2,
  Receipt,
  FileText,
  PieChart,
  RefreshCw,
  FileSearch,
  Users2,
  Box,
  FileBarChart,
  Briefcase,
  Wallet,
  FileSpreadsheet,
  UserCheck,
  ClipboardCheck,
  AlertTriangle,
  Video,
  HeartHandshake,
  UserPlus,
  Globe,
  Mail,
  Ticket,
  AlertCircle,
  Lock,
} from 'lucide-react'
import { AdminUser } from '@/lib/authService'
import { hasPermission } from '@/lib/permissions'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useSubscription } from '@/hooks/useSubscription'

// Map navigation items to subscription features they require
// undefined = always visible, string = requires that feature enabled
type SubscriptionFeature = 'finance' | 'completeFinance' | 'attendance' | 'hrPayroll' | 'mediaLibrary' | 'advancedMinistry' | 'multiLocation'

const navigation: {
  name: string
  href: string
  icon: any
  permission: string
  subscriptionFeature?: SubscriptionFeature
  children?: { name: string; href: string; icon: any }[]
}[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, permission: 'canViewDashboard' },
  { name: 'Members', href: '/members', icon: Users, permission: 'canViewMembers' },
  { name: 'Events', href: '/events', icon: Calendar, permission: 'canViewEvents' },
  { name: 'Devotions', href: '/devotions', icon: BookOpen, permission: 'canViewEvents' },
  { name: 'Reading Plans', href: '/reading-plans', icon: BookOpen, permission: 'canViewEvents' },
  { name: 'Prayer Requests', href: '/prayer-requests', icon: Heart, permission: 'canViewEvents' },
  { name: 'Fasting', href: '/fasting', icon: Sparkles, permission: 'canViewEvents' },
  { name: 'Testimonies', href: '/testimonies', icon: Sparkles, permission: 'canViewEvents' },
  { name: 'Counseling', href: '/counseling', icon: HeartHandshake, permission: 'canViewEvents', subscriptionFeature: 'advancedMinistry' },
  { name: 'Evangelism', href: '/evangelism', icon: UserPlus, permission: 'canViewEvents' },
  {
    name: 'Finance',
    href: '/finance',
    icon: DollarSign,
    permission: 'canViewDashboard',
    subscriptionFeature: 'finance',
    children: [
      { name: 'Dashboard', href: '/finance', icon: LayoutDashboard },
      { name: 'Contributions', href: '/finance/contributions', icon: TrendingUp },
      { name: 'Expenses', href: '/finance/expenses', icon: Receipt },
      { name: 'Vendors', href: '/finance/vendors', icon: Users2 },
      { name: 'Fixed Assets', href: '/finance/fixed-assets', icon: Box },
      { name: 'Budgets', href: '/finance/budgets', icon: PieChart },
      { name: 'Bank Reconciliation', href: '/finance/reconciliation', icon: RefreshCw },
      { name: 'Reports', href: '/finance/reports', icon: FileText },
      { name: 'Chart of Accounts', href: '/finance/accounts', icon: Package },
      { name: 'Journal Entries', href: '/finance/journal', icon: BarChart3 },
      { name: 'Audit Log', href: '/finance/audit-log', icon: FileSearch },
    ]
  },
  {
    name: 'HR & Payroll',
    href: '/hr',
    icon: Briefcase,
    permission: 'canViewDashboard',
    subscriptionFeature: 'hrPayroll',
    children: [
      { name: 'Employees', href: '/hr/employees', icon: Users },
      { name: 'Payroll Processing', href: '/hr/payroll', icon: Wallet },
      { name: 'Statutory Reports', href: '/hr/statutory-reports', icon: FileSpreadsheet },
    ]
  },
  {
    name: 'Attendance',
    href: '/attendance',
    icon: UserCheck,
    permission: 'canViewMembers',
    subscriptionFeature: 'attendance',
    children: [
      { name: 'Services', href: '/attendance/services', icon: Calendar },
      { name: 'Live Dashboard', href: '/attendance/dashboard', icon: ClipboardCheck },
      { name: 'Welfare Checks', href: '/attendance/welfare', icon: AlertTriangle },
    ]
  },
  { name: 'Announcements', href: '/announcements', icon: MessageSquare, permission: 'canViewEvents' },
  { name: 'Media Library', href: '/media', icon: Video, permission: 'canViewEvents', subscriptionFeature: 'mediaLibrary' },
  { name: 'Help & Support', href: '/support', icon: Ticket, permission: 'canViewDashboard' },
  { name: 'Communications', href: '/communications', icon: MessageSquare, permission: 'canViewCommunications' },
  { name: 'Admin Users', href: '/admins', icon: Shield, permission: 'canManageAdmins' },
  { name: 'Settings', href: '/settings', icon: Settings, permission: 'canManageSettings' },
]

interface AppSidebarProps {
  user: AdminUser | null
  onLogout: () => void
}

export default function AppSidebar({ user, onLogout }: AppSidebarProps) {
  const [theme, setTheme] = useState<'light' | 'dark'>('dark')
  const [churchLogo, setChurchLogo] = useState<string | null>(null)
  const [churchName, setChurchName] = useState<string>('FaithYoke')
  const location = useLocation()
  const { features } = useSubscription(user?.churchId)

  // Check if a subscription feature is available
  const isFeatureAvailable = (feature?: SubscriptionFeature): boolean => {
    if (!feature) return true // No feature gate = always available
    if (user?.adminLevel === 'super_admin') return true // Super admins bypass gates
    return features[feature] ?? true // Default to true if unknown
  }

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark'
    setTheme(savedTheme as 'light' | 'dark')
    loadChurchLogo()
  }, [user])

  async function loadChurchLogo() {
    try {
      // Super admins see platform branding, not a specific church
      if (user?.adminLevel === 'super_admin') {
        setChurchName('FaithYoke Platform')
        setChurchLogo(null) // No church logo for platform admin
        return
      }

      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return

      // Get user's church ID from profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', authUser.id)
        .single()

      if (!profile || !profile.church_id) return

      // Get church logo and name
      const { data: church } = await supabase
        .from('churches')
        .select('logo_url, name, display_name')
        .eq('id', profile.church_id)
        .single()

      if (church) {
        if (church.logo_url) {
          setChurchLogo(church.logo_url)
        }
        setChurchName(church.display_name || church.name || 'FaithYoke')
      }
    } catch (error) {
      console.error('Error loading church logo:', error)
    }
  }

  function toggleTheme() {
    const newTheme = theme === 'dark' ? 'light' : 'dark'
    setTheme(newTheme)
    localStorage.setItem('theme', newTheme)

    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  // Check if current path is within finance, HR, attendance, or platform section
  const isFinanceActive = location.pathname.startsWith('/finance')
  const isHRActive = location.pathname.startsWith('/hr')
  const isAttendanceActive = location.pathname.startsWith('/attendance')
  const isPlatformActive = location.pathname.startsWith('/platform')

  // Platform admin navigation (only for super admins)
  const platformNavigation = [
    {
      name: 'Platform Admin',
      href: '/platform',
      icon: Globe,
      permission: null, // Handled by super_admin check
      children: [
        { name: 'Dashboard', href: '/platform', icon: LayoutDashboard },
        { name: 'System Health', href: '/platform/health', icon: HeartHandshake },
        { name: 'Audit Log', href: '/platform/audit-log', icon: FileSearch },
        { name: 'Error Tracking', href: '/platform/errors', icon: AlertCircle },
        { name: 'Support Tickets', href: '/platform/support', icon: Ticket },
        { name: 'Subscriptions', href: '/platform/subscriptions', icon: DollarSign },
        { name: 'Payment History', href: '/platform/payments', icon: Receipt },
        { name: 'Churches', href: '/platform/churches/new', icon: Building2 },
        { name: 'Invitations', href: '/platform/invitations', icon: Mail },
      ]
    }
  ]

  return (
    <>
      <Sidebar>
        <SidebarHeader className="border-b">
          <div className="flex items-center gap-3 p-2">
            {churchLogo ? (
              <div className="flex h-10 w-10 items-center justify-center rounded-lg overflow-hidden bg-background">
                <img
                  src={churchLogo}
                  alt="Church Logo"
                  className="h-full w-full object-contain"
                />
              </div>
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                {user?.adminLevel === 'super_admin' ? (
                  <Globe className="h-5 w-5" />
                ) : (
                  <Church className="h-5 w-5" />
                )}
              </div>
            )}
            <div className="flex flex-col">
              <span className="text-sm font-semibold">{churchName}</span>
              <span className="text-xs text-muted-foreground">
                {user?.adminLevel === 'super_admin' ? 'Platform Administration' : 'Admin Dashboard'}
              </span>
            </div>
          </div>
        </SidebarHeader>

        <SidebarContent>
          {/* Platform Admin Section (Super Admins Only) */}
          {user?.adminLevel === 'super_admin' && (
            <SidebarGroup>
              <SidebarGroupLabel>Platform Administration</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {platformNavigation.map((item: any) => {
                    if (item.children) {
                      return (
                        <Collapsible
                          key={item.name}
                          defaultOpen={isPlatformActive}
                          className="group/collapsible"
                        >
                          <SidebarMenuItem>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton>
                                <item.icon className="h-4 w-4" />
                                <span>{item.name}</span>
                                <ChevronUp className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <SidebarMenuSub>
                                {item.children.map((child: any) => (
                                  <SidebarMenuSubItem key={child.name}>
                                    <SidebarMenuSubButton asChild>
                                      <NavLink
                                        to={child.href}
                                        className={({ isActive }) =>
                                          isActive ? 'bg-accent' : ''
                                        }
                                      >
                                        <child.icon className="h-4 w-4" />
                                        <span>{child.name}</span>
                                      </NavLink>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                ))}
                              </SidebarMenuSub>
                            </CollapsibleContent>
                          </SidebarMenuItem>
                        </Collapsible>
                      )
                    }
                    return null
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {/* Regular Navigation */}
          <SidebarGroup>
            <SidebarGroupLabel>Navigation</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navigation
                  .filter((item) => {
                    // Check if user has permission to view this item
                    if (item.permission) {
                      return hasPermission(user?.adminLevel, item.permission)
                    }
                    return true
                  })
                  .map((item) => {
                    const featureLocked = !isFeatureAvailable(item.subscriptionFeature)

                    // Handle items with children (collapsible)
                    if (item.children) {
                      const isDefaultOpen = item.href === '/finance' ? isFinanceActive :
                                           item.href === '/hr' ? isHRActive :
                                           item.href === '/attendance' ? isAttendanceActive : false
                      return (
                        <Collapsible
                          key={item.name}
                          defaultOpen={isDefaultOpen}
                          className="group/collapsible"
                        >
                          <SidebarMenuItem>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton className={featureLocked ? 'opacity-60' : ''}>
                                <item.icon className="h-4 w-4" />
                                <span>{item.name}</span>
                                {featureLocked ? (
                                  <Lock className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
                                ) : (
                                  <ChevronUp className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                                )}
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <SidebarMenuSub>
                                {item.children.map((child: any) => (
                                  <SidebarMenuSubItem key={child.name}>
                                    <SidebarMenuSubButton asChild>
                                      <NavLink
                                        to={child.href}
                                        className={({ isActive }) =>
                                          isActive ? 'bg-accent' : ''
                                        }
                                      >
                                        <child.icon className="h-4 w-4" />
                                        <span>{child.name}</span>
                                      </NavLink>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                ))}
                              </SidebarMenuSub>
                            </CollapsibleContent>
                          </SidebarMenuItem>
                        </Collapsible>
                      )
                    }

                    // Regular menu items
                    return (
                      <SidebarMenuItem key={item.name}>
                        <SidebarMenuButton asChild className={featureLocked ? 'opacity-60' : ''}>
                          <NavLink
                            to={item.href}
                            className={({ isActive }) =>
                              isActive ? 'bg-accent' : ''
                            }
                          >
                            <item.icon className="h-4 w-4" />
                            <span>{item.name}</span>
                            {featureLocked && (
                              <Lock className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
                            )}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton className="w-full">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback>
                        {user ? `${user.firstName[0]}${user.lastName[0]}` : 'AD'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-1 flex-col items-start">
                      <span className="text-sm font-medium">
                        {user ? `${user.firstName} ${user.lastName}` : 'Admin User'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {user?.adminLevel === 'super_admin' ? 'Super Admin' :
                         user?.adminLevel === 'church_owner' ? 'Church Owner' :
                         user?.adminLevel === 'church_admin' ? 'Church Admin' :
                         user?.adminLevel === 'church_elder' ? 'Church Elder' :
                         user?.adminLevel === 'ministry_leader' ? 'Ministry Leader' :
                         'Administrator'}
                      </span>
                    </div>
                    <ChevronUp className="h-4 w-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem asChild>
                    <NavLink to="/profile" className="flex items-center cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <NavLink to="/settings" className="flex items-center cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={toggleTheme}>
                    {theme === 'dark' ? (
                      <>
                        <Sun className="mr-2 h-4 w-4" />
                        Light Mode
                      </>
                    ) : (
                      <>
                        <Moon className="mr-2 h-4 w-4" />
                        Dark Mode
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive" onClick={onLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <div className="flex items-center gap-2 border-b px-4 py-2 md:hidden">
        <SidebarTrigger />
        <span className="font-semibold">FaithYoke</span>
      </div>
    </>
  )
}